<script setup>
import { storeToRefs } from 'pinia'
import { useBooksStore } from '~/stores/books'
import { useRouteStore } from '~/stores/route'
import { useDrawer } from '~/composables/useDrawer'
import {
  transformMonthFromDate,
  transformMonthFromReadable
} from '~/utils/monthFormatter'
import { getArchiveCategories } from '~/utils/archive'
import dateFormatter from '~/utils/dateFormatter'
import ArchiveCategories from '~/components/archive/ArchiveCategories'
import ArchiveLister from '~/components/archive/ArchiveLister'
import AppBanner from '~/components/banners/AppBanner'

const { $label, $capitalizeFirstLetter, $sentryCaptureException, $toast } =
  useNuxtApp()

const { updateDrawerState } = useDrawer()
const archiveCategories = getArchiveCategories()
const routeStore = useRouteStore()
const routeFrom = computed(() => routeStore.from)
const route = useRoute()
const booksStore = useBooksStore()
const { categories } = storeToRefs(booksStore)
const selectedCategory = computed(() => {
  const { slug } = route.params
  const [prevMonthCode] = categories.value([-1])

  return slug || transformMonthFromDate(prevMonthCode.value)
})
const selectedCategoryTitle = computed(() => {
  const category = transformMonthFromReadable(selectedCategory.value)
  const title = dateFormatter(category, {
    day: undefined
  })

  return $capitalizeFirstLetter(title)
})

useHead({
  title: `${$label('page.archive.title')} • ${
    selectedCategoryTitle.value
  } • Bookchoice`,
  meta: [
    {
      name: 'description',
      content: $label('page.archive.subtitle')
    }
  ]
})

function openMobileCategories() {
  const drawerOptions = {
    menuPositionClass: 'right',
    title: $label('page.archive.chooseAnotherMonthBtn'),
    component: 'ArchiveCategories',
    props: {
      selectedCategory: selectedCategory.value,
      categories: archiveCategories,
      updateDrawerState
    }
  }

  updateDrawerState({
    drawerOptions,
    isDrawerShown: true
  })
}

const { error, data: books } = await useAsyncData(
  selectedCategory.value,
  () => {
    const category = transformMonthFromReadable(selectedCategory.value)
    return booksStore.getMonthlyBooks(category)
  },
  {
    watch: [selectedCategory],
    default: () => []
  }
)

if (error.value) {
  $sentryCaptureException(error.value)
}

onMounted(() => {
  if (!books.value.length) {
    $toast.error($label('messages.notAbleToRetreiveBooks'))
  }
})
</script>

<template>
  <div>
    <BasePageTitle
      :header="$label('page.archive.title')"
      :subtitle="$label('page.archive.subtitle')"
    />

    <div class="container">
      <div class="row mb-32 mb-md-72">
        <div class="col-12 d-lg-none mb-32">
          <BaseButton class="w-100" @click="openMobileCategories">
            {{ $label('page.archive.chooseAnotherMonthBtn') }}
          </BaseButton>
        </div>
        <div class="col-2 d-none d-lg-block">
          <ArchiveCategories
            :selected-category="selectedCategory"
            :categories="archiveCategories"
            :update-drawer-state="updateDrawerState"
          />
        </div>
        <div class="col-lg-10">
          <h4 class="mb-32">
            {{ selectedCategoryTitle }}
          </h4>
          <ArchiveLister :books="books" :route-from="routeFrom" />
        </div>
      </div>
    </div>

    <div class="container banner">
      <AppBanner />
    </div>
  </div>
</template>

<style lang="scss" scoped>
header {
  position: relative;
}

svg {
  right: calc((100vw - 996px) / 2);
  bottom: 0;
}

@include media-breakpoint-down(md) {
  .banner {
    padding: 0 !important;
    max-width: unset;
  }
}
</style>
